import { Form, Formik } from 'formik';

import type { SupplierSettingsMessage } from '../../../../types/SupplierSettingsMessage';
import { Button } from '../../../cdl/Button/Button';
import { DrawerContentLayout } from '../../../cdl/Drawer/DrawerContentLayout';
import { FormikInput } from '../../../cdl/Input/FormikInput';
import { Text } from '../../../cdl/Text/Text';
import { FormikDebug } from '../../../common/form-elements/formik/FormikDebug';
import { translate } from '../../../common/helpers/translate.helper';

interface MessageSettingsFormProps {
    onSubmit: (values: SupplierSettingsMessage) => void | Promise<void>;
    supplierSettingsData: SupplierSettingsMessage;
}

interface MessageSettingsFormValues {
    orderConfirmationMessage: string;
    quotationMessage: string;
}

const getInitialValues = (supplierSettings: SupplierSettingsMessage): MessageSettingsFormValues => ({
    orderConfirmationMessage: supplierSettings.messageSettings.orderConfirmationMessage ?? '',
    quotationMessage: supplierSettings.messageSettings.quotationMessage ?? '',
});

export const MessageSettingsForm = ({ onSubmit, supplierSettingsData }: MessageSettingsFormProps) => {
    const handleSubmit = (values: MessageSettingsFormValues) =>
        onSubmit({
            emailCategorySettings: {
                offerStateChange: supplierSettingsData.emailCategorySettings.offerStateChange,
                offerUpdate: supplierSettingsData.emailCategorySettings.offerUpdate,
                newChatMessage: supplierSettingsData.emailCategorySettings.newChatMessage,
                systemReminder: supplierSettingsData.emailCategorySettings.systemReminder,
            },
            messageSettings: {
                orderConfirmationMessage: values.orderConfirmationMessage,
                quotationMessage: values.quotationMessage,
            },
            multipleOffersEnabled: supplierSettingsData.multipleOffersEnabled,
            scheduleSharingEnabled: supplierSettingsData.scheduleSharingEnabled,
        });

    return (
        <Formik<MessageSettingsFormValues>
            initialValues={getInitialValues(supplierSettingsData)}
            onSubmit={handleSubmit}
        >
            {({ isSubmitting, dirty }) => (
                <Form style={{ height: '100%', minHeight: 0 }}>
                    <DrawerContentLayout
                        isBusy={isSubmitting}
                        hasUnsavedChanges={dirty}
                        primaryAction={
                            <Button type="submit" emphasis="high" isLoading={isSubmitting}>
                                {translate('supplier.messageSettings.updateMessageSettings')}
                            </Button>
                        }
                    >
                        <Text>{translate('supplier.messageSettings.explanation')}</Text>

                        <FormikInput
                            label={translate('supplier.messageSettings.quotationMessageLabel')}
                            name="quotationMessage"
                        />

                        <FormikInput
                            label={translate('supplier.messageSettings.orderConfirmationMessageLabel')}
                            name="orderConfirmationMessage"
                        />

                        <FormikDebug />
                    </DrawerContentLayout>
                </Form>
            )}
        </Formik>
    );
};
